import { IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injectable } from '@angular/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { ChannelMonitoringStatus } from 'exports/interfaces/channel-monitoring-status.enum';
import { ChannelMonitoringStatusItem } from 'exports/interfaces/channel-monitoring-status.interface';
import { Observable, map } from 'rxjs';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService } from 'shared/services';

@Injectable({
    providedIn: 'root',
})
export class ChannelDistributionStatusService implements GridService {
    private apollo: ApolloBase;
    NAME = 'ChannelDistributionStatusService';

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
    ) {
        this.apollo = this.apolloProvider.use('distributionManagementAPI');
    }

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        const orderBy = params.request.sortModel[0]?.colId ?? 'name';
        const orderDirection = params.request.sortModel[0]?.sort?.toUpperCase() ?? 'ASC';

        return this.apollo
            .query({
                query: gql`
                    query getChannels($pageNumber: Int!, $pageSize: Int!) {
                        channels {
                            searchChannels(searchQuery: { pageNumber: $pageNumber, pageSize: $pageSize }, filtering: {}) {
                                data {
                                    name
                                    logo
                                    channelMonitoringItems {
                                        id
                                        nextRunTime
                                        nextRunTimeOutput
                                        lastRunTime
                                        lastRunTimeOutput
                                        status
                                        failReason
                                        monitoringType
                                    }
                                }
                                totalCount
                            }
                        }
                    }
                `,
                variables: {
                    pageNumber,
                    pageSize,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const returnValue = response.data.channels.searchChannels;
                    const channels = returnValue.data;
                    const processedChannels: ChannelMonitoringStatusItem[] = [];
                    channels.forEach((channel: any) => {
                        const channelMonitoringItems = channel.channelMonitoringItems;
                        if (channelMonitoringItems && channelMonitoringItems.length > 0) {
                            channelMonitoringItems.forEach((item: any) => {
                                switch (item.status) {
                                    case ChannelMonitoringStatus.UNKNOWN:
                                        item.customTooltipMessage = 'Status unknown';
                                        break;
                                    case ChannelMonitoringStatus.SCHEDULED:
                                        item.customTooltipMessage = 'Scheduled';
                                        break;
                                    case ChannelMonitoringStatus.CREATED:
                                        item.customTooltipMessage = 'Created';
                                        break;
                                    case ChannelMonitoringStatus.FAILURE:
                                        item.customTooltipMessage = item.failReason ?? 'Unknown fail reason';
                                        break;
                                }
                                processedChannels.push({
                                    channelName: channel.name,
                                    channelLogoUrl: channel.logo,
                                    id: item.id,
                                    nextRunTime: item.nextRunTime,
                                    nextRunTimeOutput: item.nextRunTimeOutput,
                                    lastRunTime: item.lastRunTime,
                                    lastRunTimeOutput: item.lastRunTimeOutput,
                                    channelMonitoringStatus: item.status,
                                    failReason: item.failReason,
                                    monitoringType: item.monitoringType,
                                    description: `exports.channelDistributionStatus.table.description.${item.monitoringType.toLowerCase()}`,
                                    customTooltipMessage: item.customTooltipMessage,
                                });
                            });
                        }
                    });
                    returnValue.data = processedChannels;
                    return returnValue;
                }),
            );
    }
}
